/**
 * TTS 音频详情抽屉（旧 TTSResourceView.vue 中的详情抽屉）：基本信息、音频、TTS 配置、生成状态、使用统计
 */
import { Alert, Badge, Descriptions, Drawer, Tag } from 'antd'
import type { TTSAudio } from '@/api/services/tts'
import { audioUrl, formatCost, formatFileSize, providerColor, statusColor, statusText } from './constants'
import styles from './TTSResourcePage.module.css'

interface TTSDetailDrawerProps {
  audio: TTSAudio | null
  onClose: () => void
}

export function TTSDetailDrawer({ audio, onClose }: Readonly<TTSDetailDrawerProps>) {
  return (
    <Drawer open={audio !== null} size={720} title="TTS音频详情" placement="right" onClose={onClose}>
      {audio && (
        <>
          <Descriptions title="基本信息" column={2} className={styles.section}>
            <Descriptions.Item label="ID">{audio.id}</Descriptions.Item>
            <Descriptions.Item label="内容哈希">
              <span className={styles.mono}>{audio.content_hash}</span>
            </Descriptions.Item>
            <Descriptions.Item label="文本内容" span={2}>
              <div className={styles.textBlock}>{audio.text_content || '（长文本未缓存）'}</div>
            </Descriptions.Item>
            <Descriptions.Item label="文本长度">{audio.text_length} 字符</Descriptions.Item>
            <Descriptions.Item label="语言代码">{audio.language_code}</Descriptions.Item>
          </Descriptions>

          <Descriptions title="音频信息" column={2} className={styles.section}>
            <Descriptions.Item label="音频播放器" span={2}>
              {/* 音频由上方「文本内容」合成，文本就是它的文字版，不另配字幕轨道 */}
              <audio // NOSONAR
                className={styles.detailAudio}
                src={audioUrl(audio.audio_url)}
                controls
              />
            </Descriptions.Item>
            <Descriptions.Item label="音频URL" span={2}>
              <a className={styles.breakAll} href={audioUrl(audio.audio_url)} target="_blank" rel="noopener noreferrer">
                {audio.audio_url}
              </a>
            </Descriptions.Item>
            <Descriptions.Item label="音频时长">{audio.audio_duration_seconds || '-'} 秒</Descriptions.Item>
            <Descriptions.Item label="文件大小">
              {audio.audio_file_size_bytes ? formatFileSize(audio.audio_file_size_bytes) : '-'}
            </Descriptions.Item>
          </Descriptions>

          <Descriptions title="TTS配置" column={2} className={styles.section}>
            <Descriptions.Item label="供应商">
              <Tag color={providerColor(audio.provider)}>{audio.provider}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="语音ID">{audio.voice_id}</Descriptions.Item>
            <Descriptions.Item label="模型">{audio.model || '-'}</Descriptions.Item>
            {audio.voice_settings && (
              <Descriptions.Item label="语音设置" span={2}>
                <pre className={styles.textBlock}>{JSON.stringify(audio.voice_settings, null, 2)}</pre>
              </Descriptions.Item>
            )}
          </Descriptions>

          <Descriptions title="生成状态" column={2} className={styles.section}>
            <Descriptions.Item label="状态">
              <Tag color={statusColor(audio.generation_status)}>{statusText(audio.generation_status)}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="生成时间">{audio.generated_at || '-'}</Descriptions.Item>
            <Descriptions.Item label="成本">{formatCost(audio.cost_usd, 6)}</Descriptions.Item>
            {audio.error_message && (
              <Descriptions.Item label="错误信息" span={2}>
                <Alert type="error" showIcon title={audio.error_message} />
              </Descriptions.Item>
            )}
          </Descriptions>

          <Descriptions title="使用统计" column={2}>
            <Descriptions.Item label="使用次数">
              <Badge count={audio.usage_count} color="green" />
            </Descriptions.Item>
            <Descriptions.Item label="最后使用">{audio.last_used_at || '从未使用'}</Descriptions.Item>
            <Descriptions.Item label="创建时间">{audio.created_at}</Descriptions.Item>
            <Descriptions.Item label="更新时间">{audio.updated_at}</Descriptions.Item>
          </Descriptions>
        </>
      )}
    </Drawer>
  )
}
